// Primitivas de UI Ona Pulse: Card, títulos, chips, filas, KPI, badges, Ring y Spark.
import React, { useEffect, useRef, useState } from 'react';
import { Animated, Modal, PanResponder, Pressable, ScrollView, StyleSheet, Text, View, type ViewStyle } from 'react-native';
import Svg, { Circle, Polyline, Text as SvgText } from 'react-native-svg';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { C, F, PMS_BADGE, R } from '@/constants/theme';
// (F se usa también en los textos SVG: sin fontFamily, el SVG cae en serifa)
import { fmtDec } from '@/lib/format';

/* `demo` marca la tarjeta con una franja ámbar. 🔴 No es decoración: en
   producción conviven módulos reales con restos del prototipo, y una nota al
   pie dentro de un modal NO basta — alguien lee «ADR 121,4 €» y se lo cree.
   Mientras un módulo no tenga datos de verdad, que se vea de lejos. */
export function Card({ children, style, demo }: { children: React.ReactNode; style?: ViewStyle; demo?: boolean }) {
  return (
    <View style={[s.card, style, demo && s.cardDemo]}>
      {demo ? (
        <View style={s.demoStrip}>
          <Text style={s.demoStripTxt}>DATOS DE EJEMPLO · AÚN NO CONECTADO</Text>
        </View>
      ) : null}
      {children}
    </View>
  );
}

/* Hoja inferior con gesto: se arrastra hacia abajo para cerrarla, como en
   cualquier app. El tirador era decorativo y la gente lo intentaba — que un
   elemento parezca arrastrable y no lo sea es una promesa incumplida.
   El gesto vive en la zona superior (tirador + cabecera) para no pelearse con
   el desplazamiento del contenido. */
/* Desde dónde entra la hoja: más que cualquier pantalla, para que empiece fuera. */
const ALTO_ENTRADA = 1000;

export function BottomSheet({ visible, onClose, children, maxAlto = '92%' }: {
  visible: boolean; onClose: () => void; children: React.ReactNode; maxAlto?: `${number}%`;
}) {
  const y = useRef(new Animated.Value(0)).current;
  /* Cuánto ha bajado el dedo COMO MÁXIMO en este arrastre. Sin esto, un gesto
     lento que baja y vuelve un poco antes de soltar se interpretaba como «no
     quería cerrar», y la hoja rebotaba de vuelta arriba. */
  const maxDy = useRef(0);
  const insets = useSafeAreaInsets();

  const pan = useRef(
    PanResponder.create({
      /* 🔴 El tirador no arrastraba y había que empezar el gesto dentro de la
         tarjeta. Dos motivos: la zona sensible medía 27 px de alto (el dedo
         caía fuera) y el responder solo se reclamaba DESPUÉS de mover, así que
         un arrastre corto se lo quedaba el contenido. Ahora se reclama al
         TOCAR y la zona ocupa 44 px de ancho completo, que es la medida mínima
         de un objetivo táctil. */
      onStartShouldSetPanResponder: () => true,
      onStartShouldSetPanResponderCapture: () => true,
      onMoveShouldSetPanResponder: (_e, g) => Math.abs(g.dy) > 2 && Math.abs(g.dy) > Math.abs(g.dx),
      onMoveShouldSetPanResponderCapture: (_e, g) => g.dy > 6 && Math.abs(g.dy) > Math.abs(g.dx),
      onPanResponderGrant: () => { maxDy.current = 0; },
      onPanResponderMove: (_e, g) => {
        if (g.dy > 0) { y.setValue(g.dy); maxDy.current = Math.max(maxDy.current, g.dy); }
      },
      onPanResponderRelease: (_e, g) => {
        /* 🔴 «SE VUELVE A SUBIR TODO SOLO Y SE BAJA SOLO». Patricio, con el vídeo
           lento del 25-08: «en vez de bajarse y seguir, se vuelve a subir todo
           solo… la función la cumple pero no queda bien, y pasa con todos». En el
           segundo 3,31 la hoja está 250 pt más abajo y en el 3,60 ya ha vuelto:
           bajaba y REBOTABA en vez de cerrarse. Tres causas sumadas:

           1. El umbral se medía SOLO con el desplazamiento final. En un arrastre
              lento y exploratorio —bajas, dudas, subes un poco y sueltas— la hoja
              había recorrido 250 pt pero `g.dy` al soltar era pequeño, así que
              decidía «no cerrar». Ahora manda el recorrido MÁXIMO: si en algún
              momento pasó del umbral y sigues abajo, se cierra. Es lo que la mano
              ha dicho, no lo que decía el último milisegundo.
           2. El umbral era demasiado alto (110 pt) para una hoja de pantalla
              completa. 78 pt es un gesto claro y no se dispara sin querer.
           3. Y la vuelta usaba un muelle con `bounciness: 4`, o sea que cuando
              NO cerraba, rebotaba. Eso es exactamente el «no queda bien»: un
              rebote elástico en algo que no se ha movido de sitio se lee como un
              fallo. Ahora vuelve con un muelle seco y rápido. */
        const recorrido = Math.max(maxDy.current, g.dy);
        if (recorrido > 78 || g.vy > 0.55) {
          /* 🔴 SIN `y.setValue(0)` ANTES DE CERRAR. Ese reset era el «efecto raro»
             que Patricio grabó y que sobrevivió a dos arreglos: la hoja bajaba
             hasta salir, el reset la DEVOLVÍA a su sitio de golpe, y el modal se
             desvanecía con la hoja entera dentro — «se vuelve a subir todo solo y
             se baja solo». Era invisible cuando el modal salía con `slide` (su
             propio deslizamiento lo tapaba) y quedó al descubierto al pasar a
             `fade` en la 0.3.65. El reset es además innecesario: el efecto de
             apertura ya coloca la hoja fuera y la hace subir cada vez. */
          Animated.timing(y, { toValue: ALTO_ENTRADA, duration: 170, useNativeDriver: true }).start(() => {
            onClose();
          });
        } else {
          Animated.spring(y, {
            toValue: 0, useNativeDriver: true, bounciness: 0, speed: 20,
          }).start();
        }
      },
    })
  ).current;

  /* 🔴 LA HOJA ENTRA DESLIZÁNDOSE ELLA, no el modal entero. Antes el `Modal` lo
     hacía con `animationType="slide"`, que mueve TODO su contenido —velo
     incluido—, así que el oscurecido llegaba barriendo desde abajo en vez de
     aparecer detrás. Ahora el velo se desvanece (`fade`) y la hoja sube sola con
     un muelle seco: es el orden natural —primero se apaga la luz, luego entra la
     hoja— y es lo que a Patricio le faltaba cuando decía «pierde el dinamismo». */
  useEffect(() => {
    if (!visible) return;
    y.setValue(ALTO_ENTRADA);
    Animated.spring(y, { toValue: 0, useNativeDriver: true, bounciness: 0, speed: 14 }).start();
  }, [visible, y]);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      {/* 🔴 EL VELO CUBRE LA PANTALLA ENTERA, no el hueco que deja la hoja. Tenía
          `flex: 1`, o sea que era un hijo en flujo colocado ENCIMA de la hoja: con
          una hoja al 92 %, el velo medía el 8 % restante. Consecuencia, y es el
          fallo que Patricio veía al deslizar: al arrastrar la hoja hacia abajo, la
          página que asomaba salía a BRILLO COMPLETO y con un borde duro, porque
          allí no había velo ninguno — parecían dos pantallas pegadas. Y al cerrar,
          el oscurecido aparecía de golpe. Con el velo cubriendo todo y la hoja
          anclada abajo en absoluto, arrastrar descubre página ATENUADA, que es lo
          que hace que el gesto se entienda. */}
      <Pressable style={s.veil} onPress={onClose} />
      <Animated.View
        style={[
          s.sheet,
          // 🔴 El margen inferior se acota: en una app añadida a la pantalla de
          // inicio, el hueco seguro que informa el sistema puede ser generoso y
          // dejaba una franja vacía enorme en los iPhone grandes.
          { paddingBottom: 18 + Math.min(insets.bottom, 22), maxHeight: maxAlto, transform: [{ translateY: y }] },
        ]}
      >
        <View {...pan.panHandlers} style={s.agarre} hitSlop={{ top: 10, bottom: 10, left: 0, right: 0 }}>
          <View style={s.grab} />
        </View>
        {/* 🔴 EL CUERPO TIENE QUE PODER ENCOGERSE. Sin esta caja, el contenido de
            la hoja —que casi siempre es un ScrollView— se medía por su ALTO
            NATURAL, sin límite: la hoja se quedaba en su 92 % pero el ScrollView
            se dibujaba más alto que ella y se salía por abajo. Resultado, el
            fallo que Patricio venía arrastrando y no sabía describir: al tocar el
            contenido aparecía un hueco muerto de ~100 pt entre el tirador y el
            título, el final de la hoja se iba fuera de la pantalla y no había
            forma de llegar a él. Se veía en el vídeo del 25-08 (segundo 3,04).
            `flexShrink` le da un alto ACOTADO, y con eso el ScrollView vuelve a
            saber cuánto mide y desplaza de verdad en vez de desbordarse.
            Pasaba en TODAS las hojas porque la culpa era de esta pieza, no de
            cada tarjeta — que es justo lo que él había observado. */}
        <View style={s.cuerpo}>{children}</View>
      </Animated.View>
    </Modal>
  );
}

export function SectionTitle({ children, dot = C.aqua, right }: { children: string; dot?: string; right?: string }) {
  return (
    <View style={s.st}>
      <View style={[s.stDot, { backgroundColor: dot }]} />
      <Text style={s.stTxt}>{children.toUpperCase()}</Text>
      {right ? <Text style={s.stRight}>{right}</Text> : null}
    </View>
  );
}

export type ChipKind = 'good' | 'bad' | 'warn' | 'mut';
const CHIP_STYLES: Record<ChipKind, { bg: string; fg: string }> = {
  good: { bg: 'rgba(52,211,153,0.14)', fg: C.good },
  bad: { bg: 'rgba(248,113,113,0.14)', fg: C.bad },
  warn: { bg: 'rgba(245,184,75,0.15)', fg: C.warn },
  mut: { bg: 'rgba(140,169,184,0.12)', fg: C.muted },
};

export function Chip({ kind = 'mut', children, unaLinea }: { kind?: ChipKind; children: string; unaLinea?: boolean }) {
  const st = CHIP_STYLES[kind];
  return (
    <View style={[s.chip, { backgroundColor: st.bg }, unaLinea && { flexShrink: 1, minWidth: 0 }]}>
      <Text
        style={[s.chipTxt, { color: st.fg, fontFamily: kind === 'mut' ? F.uiMed : F.uiSemi }]}
        numberOfLines={unaLinea ? 1 : undefined}
        ellipsizeMode="tail"
      >{children}</Text>
    </View>
  );
}

export function Row({ icon, label, value, chip, chipKind, last }: {
  icon?: React.ReactNode; label: string; value?: string; chip?: string; chipKind?: ChipKind; last?: boolean;
}) {
  /* 🔴 EL TEXTO SE SALÍA DE LA TARJETA. La etiqueta tenía `flex: 1` pero la
     pastilla de la derecha no se encogía, así que un texto largo —los nombres
     de los syncs caídos, por ejemplo— la empujaba fuera del borde y quedaba
     cortada a mitad, por encima del marco. Ahora la pastilla también cede sitio
     y su texto se recorta con puntos suspensivos dentro de la tarjeta. */
  return (
    <View style={[s.row, last && { borderBottomWidth: 0 }]}>
      {icon ? <View style={s.ricon}>{icon}</View> : null}
      <Text style={s.rowLabel} numberOfLines={1}>{label}</Text>
      {value ? <Text style={s.rowValue}>{value}</Text> : null}
      {chip ? (
        <View style={{ flexShrink: 1, minWidth: 0, maxWidth: '52%' }}>
          <Chip kind={chipKind ?? 'mut'} unaLinea>{chip}</Chip>
        </View>
      ) : null}
    </View>
  );
}

export interface KpiDetail {
  rows: Array<[string, string]>;
  foot?: string;
}

/* ══════════════════════════════════════════════════════════════════════════
   ZOOM-DETALLE — el mismo viaje al centro, pero con contenido libre.
   Patricio, sobre «Mes del grupo contra presupuesto»: «¿podemos hacer el efecto
   de la imagen 2 y mostrar los hoteles con su budget y su real con una barra de
   estado?». La «imagen 2» es este gesto, que ya existía para las tres tarjetas
   pequeñas: la tarjeta gira y crece hasta el centro.

   🔴 Se saca a una pieza aparte en vez de estirar `KpiDetail`, que solo sabe de
   pares etiqueta/valor. Aquí hacen falta 39 filas CON BARRA, y meter eso en una
   lista de pares habría sido inventar un formato de texto para dibujar una barra.
   Con `children`, cada tarjeta pinta su detalle como le corresponda y la
   animación se comparte — que es la parte que hay que mantener idéntica.

   Lleva ScrollView porque 39 hoteles no caben en una pantalla, y `bounces` en
   falso por lo mismo que las hojas: dos elásticos peleándose por el mismo dedo. */
export function ZoomDetalle({ open, onClose, valor, etiqueta, children, pie }: {
  open: boolean; onClose: () => void; valor: string; etiqueta: string;
  children: React.ReactNode; pie?: string;
}) {
  const a = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (open) {
      a.setValue(0);
      Animated.spring(a, { toValue: 1, friction: 8, tension: 50, useNativeDriver: true }).start();
    }
  }, [open, a]);
  /* 🔴 SIN ROTACIÓN 3D EN ESTA TARJETA, y es un cambio del 26-08 con captura
     delante. Se abría con el giro de las tarjetas pequeñas (rotateY 80°→0°), pero
     aquí dentro van 39 filas con barras en un ScrollView, y el giro de una
     tarjeta tan pesada pintaba justo lo que Patricio describió: «por un segundo
     vemos sombreado el lado izquierdo, luego pasa al derecho y luego
     desaparece» — los fotogramas intermedios de la perspectiva barriendo la
     pantalla como una cortina. En la tarjeta pequeña de los KPI el giro se queda
     (es ligera y ahí nació el efecto); esta abre con zoom y fundido, que
     conserva el «viaja al centro» sin estados intermedios que enseñar. */
  const scale = a.interpolate({ inputRange: [0, 1], outputRange: [0.88, 1] });
  const alfa = a.interpolate({ inputRange: [0, 1], outputRange: [0, 1], extrapolate: 'clamp' });
  /* 🔴 EL VELO ES HERMANO DE LA TARJETA, NO SU PADRE. Anoche lo puse envolviendo
     la tarjeta y metí un `Pressable` vacío alrededor de la lista para que el toque
     no llegase al velo y la cerrase. Funcionaba, pero ese Pressable **le robaba el
     gesto al ScrollView**: es lo que Patricio notó — «el desplazamiento se queda un
     poco trabado cuando deslizo de arriba para abajo».
     Con el velo como hermano en absoluto y la tarjeta encima, un toque en la
     tarjeta no llega al velo por construcción: no hace falta interceptar nada y el
     ScrollView recibe el dedo entero.
     Y aquí el rebote SÍ va: lo quité en las hojas porque peleaba con su gesto de
     cierre, pero este modal no tiene gesto — sin rebote, el final de la lista se
     siente como un tope, que es la otra mitad de lo «trabado». */
  return (
    <Modal visible={open} transparent animationType="fade" onRequestClose={onClose}>
      <View style={s.kpiVeil}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
        <Animated.View style={[s.kpiBig, { maxHeight: '86%' }, { opacity: alfa, transform: [{ scale }] }]}>
          <Text style={s.kpiBigV}>{valor}</Text>
          <Text style={s.kpiBigL}>{etiqueta}</Text>
          <ScrollView
            style={{ alignSelf: 'stretch', flexShrink: 1, marginTop: 14 }}
            showsVerticalScrollIndicator={false}
          >
            {children}
          </ScrollView>
          {pie ? <Text style={s.kpiBigFoot}>{pie}</Text> : null}
          <Text style={s.kpiBigHint}>toca fuera para cerrar</Text>
        </Animated.View>
      </View>
    </Modal>
  );
}

/* Zoom-rotación al centro: la tarjeta pequeña «viaja» al centro girando y
   agrandándose para enseñar su detalle (pedido de Patricio). */
function KpiDetailModal({ open, onClose, value, label, detail }: {
  open: boolean; onClose: () => void; value: string; label: string; detail: KpiDetail;
}) {
  const a = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (open) {
      a.setValue(0);
      Animated.spring(a, { toValue: 1, friction: 8, tension: 50, useNativeDriver: true }).start();
    }
  }, [open, a]);
  const scale = a.interpolate({ inputRange: [0, 1], outputRange: [0.45, 1] });
  const rot = a.interpolate({ inputRange: [0, 1], outputRange: ['80deg', '0deg'] });
  return (
    <Modal visible={open} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={s.kpiVeil} onPress={onClose}>
        <Animated.View style={[s.kpiBig, { transform: [{ perspective: 1000 }, { scale }, { rotateY: rot }] }]}>
          <Text style={s.kpiBigV}>{value}</Text>
          <Text style={s.kpiBigL}>{label}</Text>
          <View style={{ marginTop: 14, gap: 8, alignSelf: 'stretch' }}>
            {detail.rows.map(([l, v], i) => (
              <View key={i} style={s.kpiBigRow}>
                <Text style={s.kpiBigRowL}>{l}</Text>
                <Text style={s.kpiBigRowV}>{v}</Text>
              </View>
            ))}
          </View>
          {detail.foot ? <Text style={s.kpiBigFoot}>{detail.foot}</Text> : null}
          <Text style={s.kpiBigHint}>toca para cerrar</Text>
        </Animated.View>
      </Pressable>
    </Modal>
  );
}

export function KpiTile({ value, label, delta, deltaKind = 'good', detail }: {
  value: string; label: string; delta?: string; deltaKind?: 'good' | 'bad' | 'flat'; detail?: KpiDetail;
}) {
  const [open, setOpen] = useState(false);
  const color = deltaKind === 'good' ? C.good : deltaKind === 'bad' ? C.bad : C.muted;
  const inner = (
    <>
      <Text style={s.kpiV} numberOfLines={1}>{value}</Text>
      <Text style={s.kpiL}>{label}</Text>
      {delta ? <Text style={[s.kpiD, { color }]}>{delta}</Text> : null}
    </>
  );
  if (!detail) return <View style={s.kpi}>{inner}</View>;
  return (
    <>
      <Pressable style={s.kpi} onPress={() => setOpen(true)}>{inner}</Pressable>
      <KpiDetailModal open={open} onClose={() => setOpen(false)} value={value} label={label} detail={detail} />
    </>
  );
}

export function PmsBadge({ pms }: { pms: string }) {
  const st = PMS_BADGE[pms] ?? PMS_BADGE.noray;
  return (
    <View style={[s.pmsb, { backgroundColor: st.bg }]}>
      <Text style={[s.pmsbTxt, { color: st.fg }]}>{pms.toUpperCase()}</Text>
    </View>
  );
}

export function StatusDot({ color = C.good, size = 11 }: { color?: string; size?: number }) {
  return <View style={{ width: size, height: size, borderRadius: size / 2, backgroundColor: color }} />;
}

export function Ring({ pct, size = 62 }: { pct: number; size?: number }) {
  const r = size / 2 - 5;
  const c = 2 * Math.PI * r;
  const p = Math.min(pct, 100) / 100;
  return (
    <Svg width={size} height={size}>
      <Circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(140,200,215,0.15)" strokeWidth={6} fill="none" />
      <Circle
        cx={size / 2} cy={size / 2} r={r}
        stroke={pct >= 100 ? C.good : C.aqua} strokeWidth={6} fill="none" strokeLinecap="round"
        strokeDasharray={`${c * p} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <SvgText x={size / 2} y={size / 2 + 4.5} textAnchor="middle" fontSize={13} fontWeight="700" fontFamily={F.uiBold} fill={C.ink}>
        {`${fmtDec(pct, 0)}%`}
      </SvgText>
    </Svg>
  );
}

export function Spark({ vals, width = 72, height = 26, color = C.aqua }: {
  vals: number[]; width?: number; height?: number; color?: string;
}) {
  const mn = Math.min(...vals);
  const mx = Math.max(...vals);
  const rng = mx - mn || 1;
  const pts = vals.map((v, i) => `${((i / (vals.length - 1)) * width).toFixed(1)},${(height - 3 - ((v - mn) / rng) * (height - 8)).toFixed(1)}`);
  const [lx, ly] = pts[pts.length - 1].split(',');
  return (
    <Svg width={width} height={height}>
      <Polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" opacity={0.9} />
      <Circle cx={lx} cy={ly} r={3} fill={color} />
    </Svg>
  );
}

export function Bar({ pct, color = C.aqua, height = 7 }: { pct: number; color?: string; height?: number }) {
  return (
    <View style={{ height, backgroundColor: 'rgba(140,200,215,0.10)', borderRadius: 6, overflow: 'hidden' }}>
      <View style={{ width: `${Math.min(pct, 100)}%`, height: '100%', borderRadius: 6, backgroundColor: color }} />
    </View>
  );
}

export function SegBar({ segs }: { segs: Array<{ v: number; color: string }> }) {
  const tot = segs.reduce((a, x) => a + x.v, 0);
  return (
    <View style={{ flexDirection: 'row', height: 14, borderRadius: 8, overflow: 'hidden', gap: 2 }}>
      {segs.map((x, i) => (
        <View key={i} style={{ flex: x.v / tot, backgroundColor: x.color }} />
      ))}
    </View>
  );
}

const s = StyleSheet.create({
  card: { backgroundColor: C.card, borderColor: C.line, borderWidth: 1, borderRadius: R.card, padding: 15 },
  cardDemo: { borderColor: 'rgba(245,184,75,0.35)' },
  demoStrip: {
    alignSelf: 'flex-start', backgroundColor: 'rgba(245,184,75,0.16)',
    borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3, marginBottom: 10,
  },
  demoStripTxt: { fontFamily: F.uiBold, fontSize: 8.5, letterSpacing: 0.9, color: C.warn },
  veil: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(2,10,16,0.6)' },
  sheet: {
    /* Anclada abajo y en absoluto: con el velo cubriendo la pantalla, la hoja ya
       no puede ir en flujo detrás de él o quedaría arriba. */
    position: 'absolute', left: 0, right: 0, bottom: 0,
    backgroundColor: C.bg2, borderTopLeftRadius: R.sheet, borderTopRightRadius: R.sheet,
    paddingHorizontal: 20,
    /* Que nada se salga de la esquina redondeada. En iOS un hijo más alto que su
       padre NO se recorta solo, y era la otra mitad del desbordamiento. */
    overflow: 'hidden',
  },
  /* Alto acotado para el contenido: la hoja manda, el contenido se adapta. */
  cuerpo: { flexShrink: 1, alignSelf: 'stretch' },
  agarre: { paddingTop: 14, paddingBottom: 14, alignItems: 'center', alignSelf: 'stretch' },
  grab: { width: 46, height: 5, borderRadius: 4, backgroundColor: 'rgba(140,169,184,0.55)' },
  st: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 },
  stDot: { width: 7, height: 7, borderRadius: 4 },
  stTxt: { fontFamily: F.uiSemi, fontSize: 11, letterSpacing: 1.5, color: C.muted, flexShrink: 1 },
  stRight: { marginLeft: 'auto', fontFamily: F.uiMed, fontSize: 10, color: C.aqua },
  chip: { borderRadius: R.pill, paddingHorizontal: 10, paddingVertical: 4, alignSelf: 'flex-start' },
  chipTxt: { fontSize: 11.5 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 9, borderBottomWidth: 1, borderBottomColor: C.line },
  ricon: { width: 26, alignItems: 'center' },
  rowLabel: { flex: 1, fontFamily: F.uiMed, fontSize: 13, color: C.ink },
  rowValue: { fontFamily: F.uiBold, fontSize: 13, color: C.ink, fontVariant: ['tabular-nums'] },
  kpi: { flex: 1, backgroundColor: C.card, borderColor: C.line, borderWidth: 1, borderRadius: 16, padding: 12 },
  kpiVeil: { flex: 1, backgroundColor: 'rgba(2,10,16,0.7)', alignItems: 'center', justifyContent: 'center', padding: 28 },
  kpiBig: {
    alignSelf: 'stretch', alignItems: 'center', backgroundColor: C.bg2,
    borderColor: 'rgba(30,211,199,0.35)', borderWidth: 1, borderRadius: 24, padding: 24,
  },
  kpiBigV: { fontFamily: F.display, fontSize: 46, color: '#fff', fontVariant: ['tabular-nums'] },
  kpiBigL: { fontFamily: F.uiSemi, fontSize: 12, letterSpacing: 1.2, color: C.aquaSoft, marginTop: 4, textTransform: 'uppercase' },
  kpiBigRow: { flexDirection: 'row', justifyContent: 'space-between', gap: 12 },
  kpiBigRowL: { fontFamily: F.ui, fontSize: 13, color: C.muted },
  kpiBigRowV: { fontFamily: F.uiBold, fontSize: 13.5, color: C.ink, fontVariant: ['tabular-nums'] },
  kpiBigFoot: { fontFamily: F.ui, fontSize: 10.5, color: C.faint, marginTop: 14, textAlign: 'center', lineHeight: 15 },
  kpiBigHint: { fontFamily: F.ui, fontSize: 9.5, color: C.faint, marginTop: 10 },
  kpiV: { fontFamily: F.uiBold, fontSize: 19, color: C.ink, letterSpacing: -0.3, fontVariant: ['tabular-nums'] },
  kpiL: { fontFamily: F.ui, fontSize: 10, color: C.muted, marginTop: 3, lineHeight: 13 },
  kpiD: { fontFamily: F.uiSemi, fontSize: 10.5, marginTop: 5 },
  pmsb: { borderRadius: R.pill, paddingHorizontal: 7, paddingVertical: 2.5, alignSelf: 'flex-start' },
  pmsbTxt: { fontFamily: F.uiSemi, fontSize: 8.5, letterSpacing: 0.8 },
});
